// src/pages/Admin.js
import React, {useState, useEffect} from 'react';
import {supabase} from '../api/supabaseClient';
import {uploadImage} from '../api/cloudinary';
import ModernStats from '../components/ModerStats';
import {CardContent, InputAdornment} from '@mui/material';
import {Email as EmailIcon, Lock as LockIcon} from '@mui/icons-material';
import {YouTube as YouTubeIcon} from '@mui/icons-material';
import {
  Box,
  Drawer,
  List as MuiList,
  ListItem as MuiListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Toolbar,
  AppBar,
  Typography,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tabs,
  Tab,
  Card,
  CardMedia,
  CardActionArea,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  BarChart as BarChartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ExitToApp as ExitIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

let Swal;
const drawerWidth = 300;

const getYouTubeID = (url) => {
  const regExp = /^.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[1].length === 11) ? match[1] : null;
};

const Admin = () => {
  // AUTH
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // DATA
  const [works, setWorks] = useState([]);
  const [stories, setStories] = useState({});
  const [comments, setComments] = useState({});
  const [visits, setVisits] = useState([]);

  // ADMIN UI
  const [selectedSection, setSelectedSection] = useState('work');
  const [selectedWork, setSelectedWork] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(true);
  const [openNewModal, setOpenNewModal] = useState(false);
  const [openWorkList, setOpenWorkList] = useState(false);

  // NEW WORK
  const [newTitleEn, setNewTitleEn] = useState('');
  const [newTitleTr, setNewTitleTr] = useState('');
  const [newParentId, setNewParentId] = useState('');

  // EDIT WORK
  const [editTitleEn, setEditTitleEn] = useState('');
  const [editTitleTr, setEditTitleTr] = useState('');
  const [editParentId, setEditParentId] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [storyEn, setStoryEn] = useState('');
  const [storyTr, setStoryTr] = useState('');
  const [commentText, setCommentText] = useState('');

  // ABOUT
  const [aboutRow, setAboutRow] = useState({
    id: null,
    bio_en: '',
    bio_tr: '',
    image_url: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    instagram: '',
    twitter: '',
    facebook: ''
  });
  const [aboutEn, setAboutEn] = useState('');
  const [aboutTr, setAboutTr] = useState('');
  const [aboutImageFile, setAboutImageFile] = useState(null);
  const [aboutEmail, setAboutEmail] = useState('');
  const [aboutPhone, setAboutPhone] = useState('');
  const [aboutAddress, setAboutAddress] = useState('');
  const [aboutWebsite, setAboutWebsite] = useState('');
  const [aboutInstagram, setAboutInstagram] = useState('');
  const [aboutTwitter, setAboutTwitter] = useState('');
  const [aboutFacebook, setAboutFacebook] = useState('');

  // VIDEOS
  const [videos, setVideos] = useState([]);
  const [videoTitleEn, setVideoTitleEn] = useState('');
  const [videoTitleTr, setVideoTitleTr] = useState('');
  const [videoURL, setVideoURL] = useState('');
  const [editVideo, setEditVideo] = useState(null);

  const [expandedSeries, setExpandedSeries] = useState({});
  const toggleSeries = (id) =>
    setExpandedSeries(e => ({...e, [id]: !e[id]}));

  // AUTH LISTENER
  useEffect(() => {
    const {data: listener} = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user || null);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  // FETCH DATA
  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user]);

  const loadSwal = async () => {
    if (!Swal) {
      const mod = await import('sweetalert2');
      Swal = mod.default;
    }
  };

  const fetchVideos = async () => {
    const {data, error} = await supabase
      .from('videos')
      .select('id,title_en,title_tr,url')
      .order('inserted_at', {ascending: false});
    if (!error) setVideos(data || []);
  };

  const fetchData = async () => {
    setLoading(true);
    const {data: worksData} = await supabase.from('works').select(`*, parent_id, images(id,url), stories(id,story_en,story_tr,work_id), comments(id,text,work_id,inserted_at)`).order('created_at', {ascending: false});
    setWorks(worksData || []);

    if (selectedWork) {
      const updated = worksData.find(w => w.id === selectedWork.id);
      if (updated) {
        setSelectedWork(updated);
        setEditTitleEn(updated.title_en);
        setEditTitleTr(updated.title_tr);
        setEditParentId(updated.parent_id || '');
      }
    } else if (worksData.length) {
      selectWork(worksData[0]);
    }

    const storiesGrouped = {}, commentsGrouped = {};
    worksData.forEach(w => {
      storiesGrouped[w.id] = w.stories || [];
      commentsGrouped[w.id] = w.comments || [];
    });
    setStories(storiesGrouped);
    setComments(commentsGrouped);

    const {data: visitsData} = await supabase.from('visits').select('visit_date,work_id,count').order('visit_date', {ascending: true});
    setVisits(visitsData || []);

    const {data: aboutData} = await supabase.from('about').select('*').single();
    if (aboutData) {
      setAboutRow(aboutData);
      setAboutEn(aboutData.bio_en);
      setAboutTr(aboutData.bio_tr);
      setAboutEmail(aboutData.email);
      setAboutPhone(aboutData.phone);
      setAboutAddress(aboutData.address);
      setAboutWebsite(aboutData.website);
      setAboutInstagram(aboutData.instagram);
      setAboutTwitter(aboutData.twitter);
      setAboutFacebook(aboutData.facebook);
    }

    await fetchVideos();

    setLoading(false);
  };

  // AUTH HANDLERS
  const handleLogin = async () => {
    setLoading(true);
    await loadSwal();
    const {error} = await supabase.auth.signInWithPassword({email, password});
    if (error) Swal.fire('Error', error.message, 'error'); else Swal.fire('Success', 'Logged in', 'success');
    setLoading(false);
  };

  const handleLogout = async () => {
    setLoading(true);
    await loadSwal();
    await supabase.auth.signOut();
    setUser(null);
    setLoading(false);
  };

  // SELECTION & TOGGLING
  const selectWork = work => {
    setSelectedSection('work');
    setSelectedWork(work);
    setEditTitleEn(work.title_en);
    setEditTitleTr(work.title_tr);
    setEditParentId(work.parent_id || '');
    setTabIndex(0);
    setImageFile(null);
    setStoryEn('');
    setStoryTr('');
    setCommentText('');
  };

  const toggleDrawer = () => setOpenDrawer(p => !p);
  const toggleWorkList = () => setOpenWorkList(p => !p);

  // CRUD HANDLERS
  const handleNewWork = async () => {
    setLoading(true);
    await loadSwal();
    const {error} = await supabase.from('works').insert([{
      title_en: newTitleEn,
      title_tr: newTitleTr,
      likes: 0,
      parent_id: newParentId || null
    }]);
    if (error) Swal.fire('Error', error.message, 'error'); else {
      Swal.fire('Success', 'Work created', 'success');
      setOpenNewModal(false);
      setNewTitleEn('');
      setNewTitleTr('');
      setNewParentId('');
      fetchData();
    }
    setLoading(false);
  };

  const handleEditWork = async () => {
    if (!selectedWork) return;
    setLoading(true);
    await loadSwal();
    const {error} = await supabase.from('works').update({
      title_en: editTitleEn,
      title_tr: editTitleTr,
      parent_id: editParentId || null
    }).eq('id', selectedWork.id);
    if (error) Swal.fire('Error', error.message, 'error'); else {
      Swal.fire('Success', 'Work updated', 'success');
      fetchData();
    }
    setLoading(false);
  };

  const handleDeleteWork = async () => {
    if (!selectedWork) return;
    await loadSwal();
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: selectedWork.parent_id
        ? 'This will delete the work and all its related data.'
        : 'This will delete the series and **all** its sub-works and their data.',
      icon: 'warning',
      showCancelButton: true
    });
    if (!result.isConfirmed) return;

    setLoading(true);
    const parentId = selectedWork.id;
    let allIds = [parentId];
    if (!selectedWork.parent_id) {
      const {data: children = [], error: childrenErr} = await supabase
        .from('works')
        .select('id')
        .eq('parent_id', parentId);
      if (childrenErr) console.error('Error fetching sub-works:', childrenErr);
      else allIds = allIds.concat(children.map(w => w.id));
    }

    await supabase.from('images').delete().in('work_id', allIds);
    await supabase.from('stories').delete().in('work_id', allIds);
    await supabase.from('comments').delete().in('work_id', allIds);
    await supabase.from('works').delete().in('id', allIds);

    Swal.fire('Deleted', 'Work successfully deleted.', 'success');
    setSelectedWork(null);
    fetchData();
    setSelectedSection('work');
    setLoading(false);
  };

  const handleUploadImage = async () => {
    if (!selectedWork || !imageFile) return;
    setLoading(true);
    const url = await uploadImage(imageFile);
    const {error} = await supabase.from('images').insert([{work_id: selectedWork.id, url}]);
    if (error) Swal.fire('Error', error.message, 'error'); else {
      Swal.fire('Success', 'Image uploaded', 'success');
      fetchData();
    }
    setImageFile(null);
    setLoading(false);
  };

  const handleDeleteImage = async img => {
    await loadSwal();
    const res = await Swal.fire({title: 'Delete this image?', icon: 'warning', showCancelButton: true});
    if (res.isConfirmed) {
      setLoading(true);
      await supabase.from('images').delete().eq('id', img.id);
      Swal.fire('Deleted', 'Image successfully deleted', 'success');
      fetchData();
      setLoading(false);
    }
  };

  const isLeaf = selectedWork ? !works.some(w => w.parent_id === selectedWork.id) : false;

  const handleAddStory = async () => {
    if (!selectedWork || !isLeaf || (!storyEn.trim() && !storyTr.trim())) return;
    setLoading(true);
    const {data: newRows, error} = await supabase.from('stories').insert([{
      work_id: selectedWork.id,
      story_en: storyEn,
      story_tr: storyTr
    }]).select('*');
    if (error) Swal.fire('Error', error.message, 'error'); else {
      Swal.fire('Success', 'Story added', 'success');
      setStories(p => ({...p, [selectedWork.id]: [...p[selectedWork.id] || [], newRows[0]]}));
      setStoryEn('');
      setStoryTr('');
    }
    setLoading(false);
  };

  const handleDeleteStory = async st => {
    if (!isLeaf) return;
    await loadSwal();
    const res = await Swal.fire({title: 'Delete this story?', icon: 'warning', showCancelButton: true});
    if (res.isConfirmed) {
      setLoading(true);
      await supabase.from('stories').delete().eq('id', st.id);
      Swal.fire('Deleted', 'Story successfully deleted', 'success');
      setStories(p => ({...p, [selectedWork.id]: p[selectedWork.id].filter(s => s.id !== st.id)}));
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedWork || !isLeaf || !commentText.trim()) return;
    setLoading(true);
    const {data: newRows, error} = await supabase.from('comments').insert([{
      work_id: selectedWork.id,
      text: commentText
    }]).select('*');
    if (error) Swal.fire('Error', error.message, 'error'); else {
      Swal.fire('Success', 'Comment added', 'success');
      setComments(p => ({...p, [selectedWork.id]: [...p[selectedWork.id] || [], newRows[0]]}));
      setCommentText('');
    }
    setLoading(false);
  };

  const handleDeleteComment = async c => {
    if (!isLeaf) return;
    await loadSwal();
    const res = await Swal.fire({title: 'Delete this comment?', icon: 'warning', showCancelButton: true});
    if (res.isConfirmed) {
      setLoading(true);
      await supabase.from('comments').delete().eq('id', c.id);
      Swal.fire('Deleted', 'Comment successfully deleted', 'success');
      setComments(p => ({...p, [selectedWork.id]: p[selectedWork.id].filter(cm => cm.id !== c.id)}));
      setLoading(false);
    }
  };

  // VIDEOS CRUD
  const handleSaveVideo = async () => {
    setLoading(true);
    await loadSwal();
    if (editVideo) {
      const {error} = await supabase
        .from('videos')
        .update({title_en: videoTitleEn, title_tr: videoTitleTr, url: videoURL})
        .eq('id', editVideo.id);
      if (error) Swal.fire('Error', error.message, 'error');
      else Swal.fire('Success', 'Video updated', 'success');
    } else {
      const {error} = await supabase
        .from('videos')
        .insert([{title_en: videoTitleEn, title_tr: videoTitleTr, url: videoURL}]);
      if (error) Swal.fire('Error', error.message, 'error');
      else Swal.fire('Success', 'Video added', 'success');
    }
    setVideoTitleEn('');
    setVideoTitleTr('');
    setVideoURL('');
    setEditVideo(null);
    fetchVideos();
    setLoading(false);
  };

  const handleDeleteVideo = async (id) => {
    await loadSwal();
    const res = await Swal.fire({title: 'Delete this video?', icon: 'warning', showCancelButton: true});
    if (res.isConfirmed) {
      setLoading(true);
      await supabase.from('videos').delete().eq('id', id);
      Swal.fire('Deleted', 'Video successfully deleted', 'success');
      fetchVideos();
      setLoading(false);
    }
  };

  const handleEditVideo = (video) => {
    setEditVideo(video);
    setVideoTitleEn(video.title_en || '');
    setVideoTitleTr(video.title_tr || '');
    setVideoURL(video.url || '');
  };

  // RENDER TABS
  const renderWorkTabs = () => (
    <Box sx={{width: '100%'}}>
      <Typography variant="h5" gutterBottom>{selectedWork?.title_en}</Typography>
      <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} textColor="primary" indicatorColor="primary">
        <Tab label="Details"/>
        <Tab label="Images"/>
        {isLeaf && <Tab label="Stories"/>}
        {isLeaf && <Tab label="Comments"/>}
      </Tabs>
      <Box sx={{mt: 2}}>
        {tabIndex === 0 && (
          <Box>
            <TextField label="Title EN" value={editTitleEn} onChange={e => setEditTitleEn(e.target.value)} fullWidth
                       sx={{mb: 2}}/>
            <TextField label="Title TR" value={editTitleTr} onChange={e => setEditTitleTr(e.target.value)} fullWidth
                       sx={{mb: 2}}/>
            <FormControl fullWidth sx={{mb: 2}}>
              <InputLabel>Parent Series</InputLabel>
              <Select value={editParentId} label="Parent Series" onChange={e => setEditParentId(e.target.value)}>
                <MenuItem value="">— root —</MenuItem>
                {works.filter(w => !w.parent_id && w.id !== selectedWork.id).map(series => (
                  <MenuItem key={series.id} value={series.id}>{series.title_en}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{display: 'flex', gap: 2}}>
              <Button variant="contained" onClick={handleEditWork} disabled={loading}>Save</Button>
              <Button variant="outlined" color="error" onClick={handleDeleteWork} disabled={loading}>Delete</Button>
            </Box>
          </Box>
        )}
        {tabIndex === 1 && (
          <Box>
            <Grid container spacing={2}>
              {selectedWork?.images.map(img => (
                <Grid item xs={4} key={img.id}>
                  <Card>
                    <CardActionArea onClick={() => handleDeleteImage(img)}>
                      <CardMedia component="img" image={img.url} alt="image" sx={{height: 140}}/>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Button variant="contained" component="label" sx={{mt: 2}} startIcon={<ImageIcon/>}>
              Select Image<input type="file" hidden accept="image/*" onChange={e => setImageFile(e.target.files[0])}/>
            </Button>
            <Button variant="contained" sx={{ml: 2, mt: 2}} onClick={handleUploadImage}
                    disabled={!imageFile || loading}>Upload</Button>
          </Box>
        )}
        {isLeaf && tabIndex === 2 && (
          <Box>
            <TextField label="Story EN" value={storyEn} onChange={e => setStoryEn(e.target.value)} fullWidth multiline
                       sx={{mb: 2}}/>
            <TextField label="Story TR" value={storyTr} onChange={e => setStoryTr(e.target.value)} fullWidth multiline
                       sx={{mb: 2}}/>
            <Button variant="contained" onClick={handleAddStory} disabled={loading}>Add Story</Button>
            {stories[selectedWork.id]?.map(st => (
              <Accordion key={st.id} sx={{mt: 2}}>
                <AccordionSummary expandIcon={
                  <ExpandMoreIcon/>}><Typography>{st.story_en.slice(0, 30) + (st.story_en.length > 30 ? '…' : '')}</Typography></AccordionSummary>
                <AccordionDetails sx={{display: 'flex', justifyContent: 'space-between'}}>
                  <Typography>{st.story_en}</Typography>
                  <IconButton color="error" onClick={() => handleDeleteStory(st)}><DeleteIcon/></IconButton>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
        {isLeaf && tabIndex === 3 && (
          <Box>
            <TextField label="Comment" value={commentText} onChange={e => setCommentText(e.target.value)} fullWidth
                       multiline sx={{mb: 2}}/>
            <Button variant="contained" onClick={handleAddComment} disabled={loading}>Add Comment</Button>
            {comments[selectedWork.id]?.map(c => (
              <Box key={c.id} sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: '#f5f5f5',
                p: 1,
                mt: 2,
                borderRadius: 1
              }}>
                <Typography>{c.text}</Typography>
                <IconButton color="error" onClick={() => handleDeleteComment(c)}><DeleteIcon/></IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderStats = () => (
    <Box>
      <ModernStats works={works} stories={stories} comments={comments} visits={visits}/>
    </Box>
  );

  const renderAboutEditor = () => {
    const handleRemoveImage = async () => {
      setLoading(true);
      await loadSwal();
      const {error} = await supabase
        .from('about')
        .update({image_url: ''})
        .eq('id', aboutRow.id);
      if (error) {
        Swal.fire('Error', error.message, 'error');
      } else {
        Swal.fire('Success', 'Image removed', 'success');
        setAboutRow(prev => ({...prev, image_url: ''}));
        setAboutImageFile(null);
      }
      setLoading(false);
    };

    const handleSaveAboutContact = async () => {
      setLoading(true);
      await loadSwal();
      let imageUrl = aboutRow.image_url;
      if (aboutImageFile) {
        imageUrl = await uploadImage(aboutImageFile);
      }
      const updates = {
        bio_en: aboutEn,
        bio_tr: aboutTr,
        image_url: imageUrl,
        email: aboutEmail,
        phone: aboutPhone,
        address: aboutAddress,
        website: aboutWebsite,
        instagram: aboutInstagram,
        twitter: aboutTwitter,
        facebook: aboutFacebook,
      };
      const {error} = await supabase
        .from('about')
        .upsert({id: aboutRow.id || undefined, ...updates}, {returning: 'minimal'});
      if (error) {
        Swal.fire('Error', error.message, 'error');
      } else {
        Swal.fire('Success', 'About & Contact updated', 'success');
        setAboutRow(prev => ({...prev, ...updates}));
      }
      setLoading(false);
    };

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Edit About & Contact
        </Typography>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600}}>
          <TextField
            label="Biography (English)"
            multiline
            minRows={4}
            value={aboutEn}
            onChange={e => setAboutEn(e.target.value)}
          />
          <TextField
            label="Biography (Türkçe)"
            multiline
            minRows={4}
            value={aboutTr}
            onChange={e => setAboutTr(e.target.value)}
          />
          <Button variant="contained" component="label">
            Upload Image
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={e => setAboutImageFile(e.target.files[0])}
            />
          </Button>
          {aboutRow.image_url && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
              <Box
                component="img"
                src={aboutRow.image_url}
                alt="About"
                sx={{width: 200, borderRadius: 1, boxShadow: 2}}
              />
              <Button
                variant="outlined"
                color="error"
                onClick={handleRemoveImage}
                disabled={loading}
              >
                Remove Image
              </Button>
            </Box>
          )}
          <Typography variant="h6" sx={{mt: 3}}>
            Contact Information
          </Typography>
          <TextField
            label="Email"
            type="email"
            value={aboutEmail}
            onChange={e => setAboutEmail(e.target.value)}
            fullWidth
          />
          <TextField
            label="Phone"
            value={aboutPhone}
            onChange={e => setAboutPhone(e.target.value)}
            fullWidth
          />
          <TextField
            label="Address"
            value={aboutAddress}
            onChange={e => setAboutAddress(e.target.value)}
            fullWidth
          />
          <TextField
            label="Website (include https://)"
            value={aboutWebsite}
            onChange={e => setAboutWebsite(e.target.value)}
            fullWidth
          />
          <TextField
            label="Instagram URL"
            value={aboutInstagram}
            onChange={e => setAboutInstagram(e.target.value)}
            fullWidth
          />
          <TextField
            label="Twitter URL"
            value={aboutTwitter}
            onChange={e => setAboutTwitter(e.target.value)}
            fullWidth
          />
          <TextField
            label="Facebook URL"
            value={aboutFacebook}
            onChange={e => setAboutFacebook(e.target.value)}
            fullWidth
          />
          <Button variant="contained" onClick={handleSaveAboutContact} disabled={loading}>
            {loading ? <CircularProgress size={24}/> : 'Save About & Contact'}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderVideosEditor = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Manage Videos
      </Typography>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mb: 2, maxWidth: 600}}>
        <TextField
          label="Video Title (English)"
          value={videoTitleEn}
          onChange={e => setVideoTitleEn(e.target.value)}
        />
        <TextField
          label="Video Title (Türkçe)"
          value={videoTitleTr}
          onChange={e => setVideoTitleTr(e.target.value)}
        />
        <TextField
          label="YouTube Link"
          value={videoURL}
          onChange={e => setVideoURL(e.target.value)}
        />
        <Box sx={{display: 'flex', gap: 1}}>
          <Button
            variant="contained"
            onClick={handleSaveVideo}
            disabled={loading || !videoTitleEn || !videoTitleTr || !videoURL}
          >
            {editVideo ? "Update" : "Add"}
          </Button>
          {editVideo && (
            <Button onClick={() => {
              setEditVideo(null);
              setVideoTitleEn('');
              setVideoTitleTr('');
              setVideoURL('');
            }}>Cancel</Button>
          )}
        </Box>
      </Box>
      <Grid container spacing={2}>
        {videos.map((video) => (
          <Grid item xs={12} md={6} key={video.id}>
            <Card sx={{display: 'flex', alignItems: 'center', gap: 2, p: 1}}>
              <CardMedia
                component="img"
                image={`https://img.youtube.com/vi/${getYouTubeID(video.url)}/0.jpg`}
                alt={video.title_en || video.title_tr || ''}
                sx={{width: 120, height: 70}}
              />
              <CardContent sx={{flex: 1}}>
                <Typography variant="subtitle1">
                  {video.title_en || ''}<br/>
                  <span style={{fontWeight: 300, fontSize: 13, color: '#999'}}>{video.title_tr || ''}</span>
                </Typography>
                <Typography variant="body2" color="text.secondary"
                            sx={{wordBreak: 'break-all'}}>{video.url}</Typography>
              </CardContent>
              <IconButton onClick={() => handleEditVideo(video)}><EditIcon/></IconButton>
              <IconButton color="error" onClick={() => handleDeleteVideo(video.id)}><DeleteIcon/></IconButton>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  if (!user) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: '100%',
            borderRadius: 3,
            boxShadow: 4,
          }}
        >
          <CardContent sx={{textAlign: 'center', p: 4}}>
            <Avatar sx={{bgcolor: 'primary.main', mx: 'auto', mb: 1}}>
              <LockIcon/>
            </Avatar>
            <Typography variant="h5" gutterBottom>
              Admin Login
            </Typography>
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
              }}
              sx={{mt: 2}}
            >
              <TextField
                fullWidth
                margin="normal"
                label="Email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon/>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Password"
                type="password"
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon/>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{mt: 3, py: 1.5}}
              >
                {loading
                  ? <CircularProgress size={24}/>
                  : 'Log In'
                }
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{display: 'flex'}}>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={toggleDrawer}><MenuIcon/></IconButton>
          <Typography variant="h6">Admin Panel</Typography>
          <Box sx={{flexGrow: 1}}/>
          <IconButton color="inherit" onClick={handleLogout}><ExitIcon/></IconButton>
        </Toolbar>
      </AppBar>
      <Drawer variant="persistent" open={openDrawer} sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          top: 64,
          height: 'calc(100vh - 64px)',
          overflowY: 'auto'
        }
      }}>
        <Divider/>
        <MuiList>
          <MuiListItem button onClick={() => setOpenNewModal(true)}><ListItemIcon><AddIcon/></ListItemIcon><ListItemText
            primary="New Work"/></MuiListItem>
          <Divider/>
          <MuiListItem button onClick={toggleWorkList}><ListItemIcon><DescriptionIcon/></ListItemIcon><ListItemText
            primary="Works"/>{openWorkList ? <ExpandLessIcon/> : <ExpandMoreIcon/>}</MuiListItem>
          <Collapse in={openWorkList} timeout="auto" unmountOnExit>
            <MuiList component="div" disablePadding>
              {works.filter(w => !w.parent_id).map(series => (
                <React.Fragment key={series.id}>
                  <Divider/>
                  <MuiListItem
                    button
                    sx={{pl: 4}}
                    onClick={() => {
                      toggleSeries(series.id);    // expand/collapse the list
                      selectWork(series);          // load this parent into the editor
                    }}
                  >
                    <ListItemIcon><DescriptionIcon/></ListItemIcon>
                    <ListItemText primary={series.title_en}/>
                    {expandedSeries[series.id] ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                  </MuiListItem>
                  <Collapse in={expandedSeries[series.id]} timeout="auto" unmountOnExit>
                    <MuiList component="div" disablePadding sx={{pl: 6}}>
                      {works
                        .filter(w => w.parent_id === series.id)
                        .map(child => (
                          <MuiListItem
                            key={child.id}
                            button
                            sx={{pl: 4}}
                            onClick={() => selectWork(child)}
                          >
                            <ListItemIcon><DescriptionIcon/></ListItemIcon>
                            <ListItemText primary={child.title_en}/>
                          </MuiListItem>
                        ))}
                    </MuiList>
                  </Collapse>
                </React.Fragment>
              ))}
            </MuiList>
          </Collapse>
          <Divider/>
          <MuiListItem button
                       onClick={() => setSelectedSection('stats')}><ListItemIcon><BarChartIcon/></ListItemIcon><ListItemText
            primary="Statistics"/></MuiListItem>
          <Divider/>
          <MuiListItem button
                       onClick={() => setSelectedSection('about')}><ListItemIcon><EditIcon/></ListItemIcon><ListItemText
            primary="Edit About"/></MuiListItem>
          <Divider/>
          <MuiListItem button
                       onClick={() => setSelectedSection('videos')}><ListItemIcon><YouTubeIcon/></ListItemIcon><ListItemText
            primary="Videos"/></MuiListItem>
        </MuiList>
      </Drawer>
      <Box component="main" sx={{flexGrow: 1, p: 3, mt: 8, ml: openDrawer ? `${drawerWidth}px` : 0}}>
        {loading && <CircularProgress/>}
        {!loading && selectedSection === 'work' && selectedWork && renderWorkTabs()}
        {!loading && selectedSection === 'stats' && renderStats()}
        {!loading && selectedSection === 'about' && renderAboutEditor()}
        {!loading && selectedSection === 'videos' && renderVideosEditor()}
      </Box>
      <Dialog open={openNewModal} onClose={() => setOpenNewModal(false)}>
        <DialogTitle>New Work</DialogTitle>
        <DialogContent>
          <TextField label="Title EN" value={newTitleEn} onChange={e => setNewTitleEn(e.target.value)} fullWidth
                     sx={{mb: 2}}/>
          <TextField label="Title TR" value={newTitleTr} onChange={e => setNewTitleTr(e.target.value)} fullWidth
                     sx={{mb: 2}}/>
          <FormControl fullWidth>
            <InputLabel>Parent Series</InputLabel>
            <Select value={newParentId} label="Parent Series" onChange={e => setNewParentId(e.target.value)}>
              <MenuItem value="">— none —</MenuItem>
              {works.filter(w => !w.parent_id).map(series => (
                <MenuItem key={series.id} value={series.id}>{series.title_en}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleNewWork} disabled={loading}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admin;
